import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Instagram, MapPin, MessagesSquare, Phone } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { INSTAGRAM, LOCATION, PHONE_DISPLAY, PHONE_TEL, waLink } from "../data/site";
import { usePageMeta } from "../hooks/usePageMeta";

const INFO_CARDS = [
    {
        icon: Phone,
        label: "Phone / WhatsApp",
        value: PHONE_DISPLAY,
        href: PHONE_TEL,
        testid: "contact-phone-card",
    },
    {
        icon: MapPin,
        label: "Location",
        value: "Anishabad, Patna, Bihar",
        testid: "contact-location-card",
    },
    {
        icon: Instagram,
        label: "Instagram",
        value: "Speech Horizon",
        href: "https://www.instagram.com/speechhorizon/",
        testid: "contact-instagram-card",
    },
];

const Contact = () => {
    usePageMeta(
        "Contact Speech Horizon | Speech Therapist in Anishabad, Patna",
        "Book a speech therapy consultation at Speech Horizon, Anishabad, Patna. Call or WhatsApp 9973416270 for speech delay, autism communication, stammering, and voice therapy."
    );

    const [form, setForm] = useState({
        name: "",
        phone: "",
        email: "",
        type: "In-Person Consultation (Anishabad, Patna)",
        message: "",
    });

    const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const onSubmit = (e) => {
        e.preventDefault();
        const message = [
            "Namaste Speech Horizon! I would like to book a consultation.",
            "",
            `Name: ${form.name}`,
            `Phone: ${form.phone}`,
            `Email: ${form.email || "-"}`,
            `Preferred Consultation: ${form.type}`,
            "",
            "Message:",
            form.message || "-",
        ].join("\n");
        window.open(waLink(message), "_blank", "noopener,noreferrer");
        toast.success("Opening WhatsApp with your consultation request pre-filled.");
    };

    const inputClass =
        "w-full rounded-xl border border-navy/10 bg-mist/60 px-4 py-3.5 text-sm text-ink outline-none transition-all duration-300 placeholder:text-ink/40 focus:border-teal focus:bg-white focus:ring-2 focus:ring-teal/20";

    return (
        <div data-testid="contact-page">
            <PageHero
                eyebrow="Contact"
                lines={["Let's Start the"]}
                highlight="Conversation"
                description="Reach out to book a consultation or simply ask a question. We are here to listen and guide your family — warmly and without pressure."
            />

            <section className="bg-cream py-20 md:py-28" data-testid="contact-main-section">
                <div className="mx-auto grid max-w-7xl gap-12 px-6 md:px-8 lg:grid-cols-5">
                    <div className="lg:col-span-2">
                        <Reveal>
                            <div className="rounded-[2rem] bg-navy p-8 text-cream">
                                <img
  src="/Images/Logo_Transparent_White.png"
  alt="Speech Horizon"
  className="h-20 w-auto"
/>
                                <p className="mt-2 text-xs font-semibold uppercase tracking-[0.25em] text-aqua/70">
                                    Speech &amp; Language Therapy
                                </p>
                                <p className="mt-5 font-jakarta text-xl font-bold leading-snug text-cream/90">
                                    Better Communication. <span className="text-peach">Greater Confidence.</span>
                                </p>
                            </div>
                        </Reveal>
                        <div className="mt-6 space-y-4">
                            {INFO_CARDS.map((card, i) => {
                                const Inner = (
                                    <>
                                        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-teal-soft text-teal-dark">
                                            <card.icon size={20} strokeWidth={1.9} />
                                        </span>
                                        <div>
                                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-ink/50">
                                                {card.label}
                                            </p>
                                            <p className="mt-1 font-jakarta text-base font-bold text-navy">
                                                {card.value}
                                            </p>
                                        </div>
                                    </>
                                );
                                return (
                                    <Reveal key={card.label} delay={0.08 + i * 0.06}>
                                        {card.href ? (
                                            <a
                                                href={card.href}
                                                data-testid={card.testid}
                                                className="flex items-center gap-4 rounded-[1.5rem] border border-navy/5 bg-white p-5 transition-all duration-300 hover:-translate-y-1 hover:border-teal/25 hover:shadow-xl hover:shadow-teal/10"
                                            >
                                                {Inner}
                                            </a>
                                        ) : (
                                            <div
                                                data-testid={card.testid}
                                                className="flex items-center gap-4 rounded-[1.5rem] border border-navy/5 bg-white p-5"
                                            >
                                                {Inner}
                                            </div>
                                        )}
                                    </Reveal>
                                );
                            })}
                        </div>
                    </div>

                    <Reveal delay={0.15} className="lg:col-span-3">
                        <form
                            onSubmit={onSubmit}
                            data-testid="contact-form"
                            className="rounded-[2rem] border border-navy/5 bg-white p-7 shadow-xl shadow-navy/5 md:p-10"
                        >
                            <h2 className="font-jakarta text-2xl font-extrabold text-navy">Send an Enquiry</h2>
                            <p className="mt-2 text-sm text-ink/60">
                                Fill in your details — submitting opens WhatsApp with everything pre-filled, ready
                                to send to us.
                            </p>
                            <div className="mt-8 grid gap-5 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="contact-name" className="mb-2 block text-sm font-semibold text-navy">
                                        Name
                                    </label>
                                    <input
                                        id="contact-name"
                                        required
                                        value={form.name}
                                        onChange={update("name")}
                                        placeholder="Your full name"
                                        data-testid="contact-name-input"
                                        className={inputClass}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="contact-phone" className="mb-2 block text-sm font-semibold text-navy">
                                        Phone Number
                                    </label>
                                    <input
                                        id="contact-phone"
                                        required
                                        type="tel"
                                        value={form.phone}
                                        onChange={update("phone")}
                                        placeholder="Your phone number"
                                        data-testid="contact-phone-input"
                                        className={inputClass}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="contact-email" className="mb-2 block text-sm font-semibold text-navy">
                                        Email
                                    </label>
                                    <input
                                        id="contact-email"
                                        type="email"
                                        value={form.email}
                                        onChange={update("email")}
                                        placeholder="you@example.com"
                                        data-testid="contact-email-input"
                                        className={inputClass}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="contact-type" className="mb-2 block text-sm font-semibold text-navy">
                                        Preferred Consultation Type
                                    </label>
                                    <select
                                        id="contact-type"
                                        value={form.type}
                                        onChange={update("type")}
                                        data-testid="contact-type-select"
                                        className={inputClass}
                                    >
                                        <option>In-Person Consultation (Anishabad, Patna)</option>
                                        <option>Online Video Consultation</option>
                                        <option>Phone Consultation</option>
                                    </select>
                                </div>
                                <div className="sm:col-span-2">
                                    <label htmlFor="contact-message" className="mb-2 block text-sm font-semibold text-navy">
                                        Message
                                    </label>
                                    <textarea
                                        id="contact-message"
                                        rows={5}
                                        value={form.message}
                                        onChange={update("message")}
                                        placeholder="Tell us briefly about your concerns — for example, your child's age and what you have noticed."
                                        data-testid="contact-message-input"
                                        className={`${inputClass} resize-none`}
                                    />
                                </div>
                            </div>
                            <button
                                type="submit"
                                data-testid="contact-submit-btn"
                                className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-peach px-8 py-4 font-jakarta text-sm font-bold text-navy shadow-xl shadow-peach/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-peach-deep hover:text-cream sm:w-auto"
                            >
                                Send via WhatsApp
                                <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </button>
                            <p className="mt-4 text-xs leading-relaxed text-ink/50">
                                Prefer to talk directly? Call or WhatsApp us anytime at{" "}
                                <a href={PHONE_TEL} className="font-semibold text-teal-dark hover:underline" data-testid="contact-direct-phone-link">
                                    {PHONE_DISPLAY}
                                </a>
                                .
                            </p>
                        </form>
                    </Reveal>
                </div>
            </section>

            <section className="bg-mist py-20 md:py-24" data-testid="contact-map-section">
                <div className="mx-auto max-w-7xl px-6 md:px-8">
                    <Reveal>
                        <div className="overflow-hidden rounded-[2rem] border-[6px] border-white shadow-2xl shadow-navy/10">
                            <iframe
                                title="Speech Horizon — Anishabad, Patna on Google Maps"
                                src="https://maps.google.com/maps?q=Anishabad,%20Patna,%20Bihar&t=&z=14&ie=UTF8&iwloc=&output=embed"
                                className="h-[380px] w-full"
                                loading="lazy"
                                data-testid="contact-map"
                            />
                        </div>
                        <p className="mt-5 text-center text-sm font-semibold text-ink/60">
                            <MapPin size={15} className="mr-1.5 inline text-teal" />
                            Visit us at Anishabad, Patna, Bihar
                        </p>
                    </Reveal>
                </div>
            </section>
        </div>
    );
};

export default Contact;
